import { jsx } from '@emotion/react';
import { Typography } from '@recruit-robin/robin-theme/web-components';
import type { ThemeFontCallback } from '@recruit-robin/robin-theme/web-styles';

import { useStyles } from './ApplyText.styles';
import type { ApplyTextAs, ApplyTextRole, ApplyTextTone } from './ApplyText.types';

/**
 * Which robin-theme variant each role builds on. Size, line-height, tracking, weight and
 * case come from the role css, which is where the styleguide's numbers live; the family
 * comes from `ApplyStage`, which outranks the variant's Outfit.
 */
const ROLE_VARIANT: Record<ApplyTextRole, ThemeFontCallback> = {
  display: (fonts) => fonts.heading[1],
  title: (fonts) => fonts.heading[2],
  item: (fonts) => fonts.body.long,
  body: (fonts) => fonts.body.long,
  sub: (fonts) => fonts.body.short,
  choice: (fonts) => fonts.body.short,
  label: (fonts) => fonts.supporting['label'],
  meta: (fonts) => fonts.supporting['helper'],
  note: (fonts) => fonts.supporting['helper'],
  cta: (fonts) => fonts.supporting['label-tiny'],
};

export type ApplyTextProps = {
  role: ApplyTextRole;
  /** Defaults to `ink-soft` for body/sub, `muted` for label/meta/note, and `ink` otherwise. */
  tone?: ApplyTextTone;
  /**
   * Semantic element to wrap the text in. Omit for a bare span — most copy in the flow
   * is a label or a value, not a paragraph.
   */
  as?: ApplyTextAs;
  align?: 'start' | 'center';
  id?: string;
  className?: string;
  children: React.ReactNode;
};

/** Body and descriptions carry the middle grey; everything else is ink. */
const DEFAULT_TONE: Record<ApplyTextRole, ApplyTextTone> = {
  display: 'ink',
  title: 'ink',
  item: 'ink',
  body: 'ink-soft',
  sub: 'ink-soft',
  choice: 'ink',
  label: 'muted',
  meta: 'muted',
  note: 'muted',
  cta: 'paper',
};

/**
 * Every string in the apply flow renders through here.
 *
 * It wraps robin-theme's `Typography` rather than replacing it, so the repo's "no raw
 * `<h2>`/`<p>`/`<span>` for text" rule holds. `Typography` renders a div or a span and takes no element prop, so semantics
 * come from an outer element and the text itself is always rendered inline — a span
 * inside an `<h1>` is valid where a div would not be.
 */
export const ApplyText = ({ role, tone, as, align, id, className, children }: ApplyTextProps) => {
  const classes = useStyles({ role, tone: tone ?? DEFAULT_TONE[role], align });

  const text = (
    <Typography
      inline
      variant={ROLE_VARIANT[role]}
      // Colour comes from the role css, which reads the flow's own tokens rather than
      // robin-theme's palette.
      color="currentColor"
      customCss={classes.text}
      className={as ? undefined : className}>
      {children}
    </Typography>
  );

  if (!as) return id ? <span id={id}>{text}</span> : text;

  // `jsx` rather than `React.createElement`: only emotion's factory reads the `css` prop
  // off a dynamically named element.
  return jsx(as, { id, className, css: classes.wrapper }, text);
};
